import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'store';
import { currentUserSelector } from 'modules/user/userSlice';
import createEventApi from 'api/eventOffset';
import createOrderApi from 'api/order';
import { showToast } from 'modules/app/toasterSlice';
import createFlightApi from 'api/flightOffset';
import getPlan from 'utils/planMapper';
import { syncAttribution } from 'modules/user/attributionSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { setTag, tags } from 'utils/tags';

export interface EventFlight {
  length: string;
  distance: number;
  flightClass: string;
  roundTrip: boolean;
  travellers: number;
  kilosCo2: number;
  price: number;
  currency: string;
  vat?: number;
}

interface State {
  people?: number;
  days?: number;
  country: string;
  price?: number;
  currency?: string;
  kilosCo2?: number;
  isLoading: boolean;
  flights: EventFlight[];
  plan: string;
  orderNumber?: string;
  orderDate?: string;
  vat?: number;
}

export const flightLengthOptions: { [x: string]: number } = {
  short: 1500,
  medium: 4000,
  long: 8000,
  ultra: 14000,
};

export const getFlightLenghtByDistance = (value: number): string => {
  if (value <= 1500) {
    return 'short';
  } else if (value <= 4000) {
    return 'medium';
  } else if (value <= 8000) {
    return 'long';
  }
  return 'ultra';
};

const initialState: State = {
  isLoading: false,
  country: 'NO',
  flights: [],
  plan: 'ClimatePositive',
};

const slice = createSlice({
  name: 'personalEventOffset',
  initialState,
  reducers: {
    changePeople: (state: State, action: PayloadAction<number>): State => {
      state.people = action.payload;
      return state;
    },
    changeDays: (state: State, action: PayloadAction<number>): State => {
      state.days = action.payload;
      return state;
    },
    changeCountry: (state: State, action: PayloadAction<string>): State => {
      state.country = action.payload;
      return state;
    },
    changeLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    changePriceAndOffset: (state: State, action: PayloadAction<{ price: number; currency: string; kilosCo2: number; vat?: number }>): State => {
      state.currency = action.payload.currency;
      state.price = action.payload.price;
      state.kilosCo2 = action.payload.kilosCo2;
      state.vat = action.payload.vat;
      return state;
    },
    addFlight: (state: State, action: PayloadAction<EventFlight>): State => {
      state.flights.push(action.payload);
      return state;
    },
    removeFlight: (state: State, action: PayloadAction<number>): State => {
      state.flights = state.flights.filter((_, index) => index !== action.payload);
      return state;
    },
    updateFlight: (state: State, action: PayloadAction<{ index: number; flight: EventFlight }>): State => {
      state.flights = state.flights.map((flight, index) => (index !== action.payload.index ? flight : action.payload.flight));
      return state;
    },
    removeAllFlights: (state: State): State => {
      state.flights = [];
      return state;
    },
    changePlan: (state: State, action: PayloadAction<string>): State => {
      state.plan = action.payload;
      return state;
    },
    changeOrder: (state: State, action: PayloadAction<{ orderNumber: string; orderDate: string }>): State => {
      state.orderDate = action.payload.orderDate;
      state.orderNumber = action.payload.orderNumber;
      return state;
    },
    restoreToDefaults: (): State => {
      return { ...initialState };
    },
  },
});

const {
  changePeople,
  changeDays,
  changeCountry,
  changeLoading,
  changePriceAndOffset,
  addFlight,
  updateFlight,
  removeFlight,
  removeAllFlights,
  changePlan,
  changeOrder,
  restoreToDefaults,
} = slice.actions;
export { changePeople, changeDays, changeCountry, removeFlight, removeAllFlights, changePlan, changeOrder, restoreToDefaults };

export default slice.reducer;

export const getOffsetPrice = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventApi = createEventApi(context);

  const { days, people, country, plan } = getState().personalEventOffset;
  if (!days || !people) {
    return;
  }

  const defaultPortfolio = defaultPortfolioSelector(getState());
  try {
    dispatch(changeLoading(true));
    const { multiplier } = getPlan(plan);
    const offset = await eventApi.getPercapitaFootprint(days, people, country);
    const kilosCo2 = offset.kilosCo2e ?? offset.kilosCo2;

    const eventData = await eventApi.getOffsetEventPrice(
      {
        kilosCo2: kilosCo2 * multiplier,
        eventPersons: people,
      },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(
      changePriceAndOffset({
        price: eventData.salesPrice,
        currency: eventData.salesCurrency,
        kilosCo2,
        vat: eventData.vat,
      }),
    );
    dispatch(changeLoading(false));
  } catch (err) {
    dispatch(changeLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const addEventFlight = (): AppThunk => async (dispatch, getState): Promise<void> => {
  try {
    const currentUser = currentUserSelector(getState());
    const context = appContextSelector(getState());
    const eventApi = createEventApi(context);
    const flightApi = createFlightApi(context);
    const defaultPortfolio = defaultPortfolioSelector(getState());

    dispatch(changeLoading(true));
    const newFlight: EventFlight = {
      distance: flightLengthOptions['medium'],
      flightClass: 'economy',
      length: 'medium',
      roundTrip: false,
      travellers: 1,
      kilosCo2: 0,
      price: 0,
      currency: '',
    };

    const data = await flightApi.getFlightOffset({
      roundTrip: newFlight.roundTrip,
      travelClass: newFlight.flightClass as TravelClass,
      travellers: newFlight.travellers,
      distance: newFlight.distance,
    });

    newFlight.kilosCo2 = data.kilosCo2e ?? data.kilosCo2;

    const paxMultiplier = newFlight.roundTrip ? 2 : 1;
    const priceData = await eventApi.getOffsetEventPrice(
      { eventFlightPax: paxMultiplier * newFlight.travellers, kilosCo2: newFlight.kilosCo2 },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );
    newFlight.price = priceData.salesPrice;
    newFlight.currency = priceData.salesCurrency;
    newFlight.vat = priceData.vat;

    dispatch(addFlight(newFlight));
    dispatch(changeLoading(false));
  } catch {
    dispatch(changeLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const updateEventFlight = (flightToUpdate: EventFlight, index: number): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventApi = createEventApi(context);
  const flightApi = createFlightApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    dispatch(changeLoading(true));
    const data = await flightApi.getFlightOffset({
      roundTrip: flightToUpdate.roundTrip,
      travelClass: flightToUpdate.flightClass as TravelClass,
      travellers: flightToUpdate.travellers,
      distance: flightToUpdate.distance,
    });

    flightToUpdate.kilosCo2 = data.kilosCo2e ?? data.kilosCo2;

    const paxMultiplier = flightToUpdate.roundTrip ? 2 : 1;
    const priceData = await eventApi.getOffsetEventPrice(
      { eventFlightPax: paxMultiplier * flightToUpdate.travellers, kilosCo2: flightToUpdate.kilosCo2 },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );
    flightToUpdate.price = priceData.salesPrice;
    flightToUpdate.currency = priceData.salesCurrency;
    flightToUpdate.vat = priceData.vat;

    dispatch(updateFlight({ index, flight: flightToUpdate }));
    dispatch(changeLoading(false));
  } catch {
    dispatch(changeLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const createOrder = (errorCallback: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(changeLoading(true));
  const { personalEventOffset, user } = getState();
  const { multiplier } = getPlan(personalEventOffset.plan);
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  const flightItems = personalEventOffset.flights.map(flight => ({
    type: 'impact',
    impact: 'Co2',
    kilosCo2: flight.kilosCo2 ?? 0,
    price: flight.price ?? 0,
    portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
    feeFactors: {
      eventFlightPax: (flight.roundTrip ? 2 : 1) * flight.travellers,
      flight: true,
    },
    details: {
      flight: {
        passengers: flight.travellers,
        flights: 1,
        roundTrip: flight.roundTrip,
        travelClass: flight.flightClass as TravelClass,
        routeLegs: [
          {
            distanceKm: flight.distance,
          },
        ],
      },
    },
  }));

  try {
    const orderResult = await orderApi.create(
      {
        currency: personalEventOffset.currency ?? '',
        comment: '',
        description: '',
        category: 'Events',
        items: [
          {
            type: 'impact',
            impact: 'Co2',
            kilosCo2: (personalEventOffset.kilosCo2 ?? 0) * multiplier,
            price: personalEventOffset.price ?? 0,
            portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
            feeFactors: {
              eventPersons: personalEventOffset.people ?? 0,
            },
            details: {
              person: {
                days: personalEventOffset.days,
                countryCode: personalEventOffset.country,
                personCount: personalEventOffset.people,
                plan: personalEventOffset.plan,
              },
            },
          },
          ...flightItems,
        ],
        payment: {
          type: 'invoice',
        },
        metadata: {
          order: {
            channel: 'mychooose-fulloffset',
          },
          event: {
            days: personalEventOffset.days ?? 0,
            personCount: personalEventOffset.people ?? 0,
            flightTravelersCount: personalEventOffset.flights.reduce((prev, curr) => prev + curr.travellers, 0),
          },
        },
      },
      user.currentAccount?.customerId ?? '',
    );
    setTag({
      ...tags.portalOffset.placeOrder,
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Event offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'event-offset',
      })),
    });
    dispatch(changeLoading(false));
    dispatch(
      changeOrder({
        orderNumber: orderResult.order.choooseId,
        orderDate: orderResult.order.createdDate,
      }),
    );
    dispatch(syncAttribution());
  } catch (err) {
    dispatch(changeLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
    errorCallback();
  }
};

export const totalPersonalEventKilosCo2Selector = createSelector(
  (state: RootState) => state.personalEventOffset,
  ({ kilosCo2, flights, plan }) => {
    const { multiplier } = getPlan(plan);
    const eventKilosCo2 = kilosCo2 ?? 0;
    const flightsKilosCo2 = flights.reduce<number>((prev, curr) => prev + curr.kilosCo2, 0);
    return (eventKilosCo2 + flightsKilosCo2) * multiplier;
  },
);

export const totalPersonalEventPriceSelector = createSelector(
  (state: RootState) => state.personalEventOffset,
  ({ price, flights }) => {
    const eventPrice = price ?? 0;
    const flightsPrice = flights.reduce<number>((prev, curr) => prev + curr.price, 0);
    return eventPrice + flightsPrice;
  },
);

export const personalEventCurrencySelector = createSelector(
  (state: RootState) => state.personalEventOffset,
  ({ currency, flights }) => {
    const flightCurrency = flights.length > 0 ? flights[0].currency : undefined;
    return currency || flightCurrency;
  },
);

export const totalPersonalEventVatSelector = createSelector(
  (state: RootState) => state.personalEventOffset,
  ({ vat, flights }) => {
    const eventVat = vat ?? 0;
    const flightsVat = flights.reduce<number>((prev, curr) => prev + (curr.vat ?? 0), 0);
    return eventVat + flightsVat;
  },
);
